import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CloudUpload, Edit } from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import DailyHoldingDetailView from "./DailyHoldingDetailView";
import DailyHoldingFormDialog from "./DailyHoldingFormDialog";
import ButtonTwo from "../../common/dashboard-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_DAILY_HOLDINGS_ENDPOINT } from "../../config/urls";

const fmt = (n) =>
  `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => `${Number(n).toFixed(2)}%`;

const normalizeHolding = (h) => ({
  ...h,
  dividend:            h.divident,
  dividend_reinvested: h.divident_reinvested,
});


const DailyHoldings = ({ onNavigate }) => {
  const { getValidToken } = useAuth();
  const [holdings, setHoldings]       = useState([]);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount]   = useState(0);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState("");
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData]       = useState(null);

  const getHeaders = useCallback(async () => {
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchHoldings = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: rowsPerPage, offset: page * rowsPerPage });
    if (search.trim()) params.set("search", search.trim());

    try {
      const headers = await getHeaders();
      const res  = await fetch(`${MFD_DAILY_HOLDINGS_ENDPOINT}?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to fetch holdings");
      const data = await res.json();
      setHoldings((data.results ?? []).map(normalizeHolding));
      setTotalCount(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching daily holdings:", err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, getHeaders]);

  useEffect(() => {
    fetchHoldings();
  }, [fetchHoldings]);

  const handleEdit = (row) => { setEditData(row); setIsModalOpen(true); };

  const handleSave = async (form) => {
    const url = `${MFD_DAILY_HOLDINGS_ENDPOINT}${editData.id}/`;
    setSaving(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(url, { method: "PATCH", headers, body: JSON.stringify(form) });
      if (!res.ok) throw new Error("Failed to save holding");
      setIsModalOpen(false);
      fetchHoldings();
    } catch (err) {
      console.error("Error saving holding:", err);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { id: "date",                 label: "Date",                  minWidth: 120 },
    { id: "first_investment_date", label: "First Investment Date", minWidth: 160 },
    { id: "pan",            label: "PAN",             minWidth: 130 },
    { id: "client_code",    label: "Client Code",     minWidth: 120 },
    {
      id: "investment",
      label: "Investment",
      minWidth: 140,
      align: "right",
      render: (r) => fmt(r.investment),
    },
    {
      id: "portfolio_value",
      label: "Portfolio Value",
      minWidth: 150,
      align: "right",
      render: (r) => fmt(r.portfolio_value),
    },
    {
      id: "xirr",
      label: "XIRR (%)",
      minWidth: 120,
      align: "right",
      render: (r) => (
        <Typography
          sx={{
            ...typography.bodyLarge,
            fontWeight: 500,
            color: r.xirr === null ?
                    ColorsConstants.primaryGray70 :
                    r.xirr >= 0 ?
                      ColorsConstants.successText :
                      ColorsConstants.error,
          }}
        >
          {r.xirr === null ? "N/A" : pct(r.xirr)}
        </Typography>
      ),
    },
    { id: "source", label: "Source", minWidth: 120 },
    {
      id: "actions",
      label: "Actions",
      minWidth: 80,
      render: (row) => (
        <Stack direction="row">
          <IconButton
            size="medium"
            sx={{ color: ColorsConstants.primaryBlue100 }}
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
          >
            <Edit fontSize="medium" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  if (selectedHolding) {
    return (
      <DailyHoldingDetailView
        holding={selectedHolding}
        onBack={() => setSelectedHolding(null)}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 },}}
>
      <Box sx={{ display: "flex",flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", gap: 2, mb: 2, alignItems: { xs: "stretch", sm: "center" }, }}>
        <Box sx={{ flex: 1, width: "100%", maxWidth: { xs: "100%", sm: 500 }, }}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder={
              isMobile
              ? "Search..."
              : "Search by PAN, Client Code, Investment or Source"
            }
          />
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} >
          <ButtonTwo  sx={{ width: { xs: "100%", sm: "auto" } }}
            startIcon={<CloudUpload />}
            onClick={() => onNavigate?.("mutual_fund_distributor.file_upload", { type: "daily_holdings" })}
          >
            Upload
          </ButtonTwo>
        </Stack>
      </Box>

      <AppTable
        columns={columns}
        rows={holdings}
        rowKey="id"
        loading={loading}
        emptyMessage="No holdings found"
        maxHeight={620}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto" }}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        onRowClick={setSelectedHolding}
      />

      <DailyHoldingFormDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        editData={editData}
        saving={saving}
      />
    </Box>
  );
};

export default DailyHoldings;
